import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Modal, Spin } from "antd";
import { useAgentsStore } from "../../../../../../../../../../../../store/agents.store";
import { selectDraftProducts } from "../../../../../../../../../../../../store/agentDetail/agentDetailSelectors";
import {
    AgentDetailSectionEnum,
    IAgentProductDetails,
} from "../../../../../../../../../../../../agentDetail.model";
import {
    AGENT_PRODUCTS_SAVE_SCOPE,
    DEFAULT_CARRIER_NAME,
} from "../../../../../../../../../../../../agents.constants";
import { useAgentDetailSave } from "../../../../../../../../../../hooks/useAgentDetailSave";
import { useAddProduct } from "./addProduct.hooks";
import {
    buildInitialSelections,
    getPrefillWritingNumber,
} from "./addProduct.utils";
import { ProductSelection, ProductSelections } from "./components/productSelection/productSelection.index";
import { Footer } from "./components/footer/footer.index";
import styles from "./addProduct.module.css";

interface IAddProductProps {
    open: boolean;
    onClose: () => void;
}

export const AddProduct = ({ open, onClose }: IAddProductProps) => {
    const existingProducts = useAgentsStore(selectDraftProducts);
    const parentUserId = useAgentsStore(
        (state) => state.detail.data?.personalInformation?.parentUserId ?? null
    );
    const updateDraftField = useAgentsStore((state) => state.updateDraftField);
    const resetDraftScope = useAgentsStore((state) => state.resetDraftScope);
    const { save, isSaving } = useAgentDetailSave(AGENT_PRODUCTS_SAVE_SCOPE);

    const { productsAndCommissions, productLoading, fetchProductsAndCommissions, productFetchError } = useAddProduct({
        parentUserId,
    });

    const [agentWritingNumber, setAgentWritingNumber] = useState("");
    const [selections, setSelections] = useState<ProductSelections>({});

    const storeWritingNumber = getPrefillWritingNumber(existingProducts);

    const assignedProductUids = useMemo(
        () => new Set(existingProducts.map((product) => product.productUid)),
        [existingProducts]
    );

    const resetForm = useCallback(() => {
        setAgentWritingNumber("");
        setSelections({});
    }, []);

    useEffect(() => {
        if (!open) {
            resetForm();
            return;
        }

        setSelections(buildInitialSelections(existingProducts));
        setAgentWritingNumber(storeWritingNumber);

        fetchProductsAndCommissions();
        // Initialize only when the modal opens so in-progress edits are not wiped.
    }, [open]);

    const handleToggle = useCallback(
        (productUid: string, checked: boolean) => {
            if (assignedProductUids.has(productUid)) return;

            setSelections((prev) => {
                if (!checked) {
                    const next = { ...prev };
                    delete next[productUid];
                    return next;
                }

                return {
                    ...prev,
                    [productUid]: null,
                };
            });
        },
        [assignedProductUids]
    );

    const handleCommissionChange = useCallback(
        (productUid: string, commissionUid: string) => {
            if (assignedProductUids.has(productUid)) return;

            setSelections((prev) => ({
                ...prev,
                [productUid]: commissionUid,
            }));
        },
        [assignedProductUids]
    );

    const newProductUids = useMemo(
        () => Object.keys(selections).filter((uid) => !assignedProductUids.has(uid)),
        [selections, assignedProductUids]
    );

    const isNumbersDirty =
        agentWritingNumber !== storeWritingNumber

    const newProductsComplete = newProductUids.length > 0 && newProductUids.every((uid) => Boolean(selections[uid]));

    const hasIncompleteNewProduct = newProductUids.some((uid) => !selections[uid]);

    const canSave =
        !hasIncompleteNewProduct &&
        (newProductsComplete || (isNumbersDirty && existingProducts.length > 0));


    const handleCancel = () => {
        resetForm();
        onClose();
    };

    const handleAdd = async () => {
        if (!canSave || isSaving) return;

        const nextWritingNumber = agentWritingNumber === '' ? null : agentWritingNumber;

        const nextProducts: IAgentProductDetails[] = [];

        for (const [productUid, commissionUid] of Object.entries(selections)) {
            if (!commissionUid) return;

            const availableProduct = productsAndCommissions.find(
                (product) => product.productUid === productUid
            );
            const existingProduct = existingProducts.find(
                (product) => product.productUid === productUid
            );
            const commission = availableProduct?.productCommissions.find(
                (item) => item.commissionUid === commissionUid
            );

            if (existingProduct) {
                nextProducts.push({
                    ...existingProduct,
                    commissionUid,
                    productCommissionLevel:
                        commission?.commissionName ?? existingProduct.productCommissionLevel,
                    agentWritingNumber: nextWritingNumber
                });
                continue;
            }

            if (!availableProduct || !commission) return;

            nextProducts.push({
                productUid: availableProduct.productUid,
                productName: availableProduct.productName,
                commissionUid: commission.commissionUid,
                productCommissionLevel: commission.commissionName,
                agentWritingNumber: nextWritingNumber,
                agencyNumber: null,
                isApproved: true,
                carrier: DEFAULT_CARRIER_NAME,
            });
        }

        // Preserve assigned products that are no longer in the available catalog
        existingProducts.forEach((product) => {
            const stillPresent = nextProducts.some((item) => item.productUid === product.productUid);
            const stillAvailable = productsAndCommissions.some(
                (item) => item.productUid === product.productUid
            );

            if (!stillPresent && !stillAvailable) {
                nextProducts.push({
                    ...product,
                    agentWritingNumber: nextWritingNumber
                });
            }
        });

        updateDraftField(
            AgentDetailSectionEnum.CONTRACT_AND_LICENSING_INFORMATION,
            "agentProductDetails",
            nextProducts
        );

        const success = await save();
        if (!success) {
            resetDraftScope(AGENT_PRODUCTS_SAVE_SCOPE);
            return;
        }

        handleCancel();
    };

    return (
        <Modal
            open={open}
            onCancel={handleCancel}
            title="Add New Product"
            footer={
                <Footer
                    handleAdd={handleAdd}
                    handleCancel={handleCancel}
                    canSave={canSave}
                    isSaving={isSaving}
                />
            }
            centered
            width={600}
            zIndex={1200}
            className={styles.modal}
            classNames={{
                body: styles.modalBody,
                title: styles.modalTitle,
            }}
            closable={!isSaving}
            mask={{ closable: false }}
            keyboard={!isSaving}
        >
            {productLoading ? (
                <div className={styles.loadingCtn}>
                    <Spin size="large" />
                </div>
            ) : (
                <div className={styles.outerCtn}>
                    <div className={styles.carrierCtn}>
                        <h4 className={styles.carrierTitle}>Carrier: {DEFAULT_CARRIER_NAME}</h4>
                    </div>
                    <div className={styles.inputCtn}>
                        <div className={styles.item}>
                            <div className={styles.label}>Agent Writing Number</div>
                            <div className={styles.value}>
                                <Input
                                    placeholder="Enter Agent Writing Number"
                                    value={agentWritingNumber}
                                    onChange={(e) => setAgentWritingNumber(e.target.value)}
                                    className={styles.input}
                                    disabled={isSaving}
                                    allowClear
                                />
                            </div>
                        </div>
                    </div>
                    <ProductSelection
                        products={productsAndCommissions}
                        selections={selections}
                        assignedProductUids={assignedProductUids}
                        fetchError={productFetchError}
                        onRetry={fetchProductsAndCommissions}
                        onToggle={handleToggle}
                        onCommissionChange={handleCommissionChange}
                    />
                </div>
            )}
        </Modal>
    );
};
